import { enqueueSnackbar } from 'notistack';
import { RESPONSIBLE_CHARGE_ID_VALUE } from './modules/ResponsiblePartiesAndContractors/hooks/useResponsibleFieldsConfig';
import { SPECIAL_USAGES_IDS } from './modules/SubstancesAndPreventiveMeasures/hooks/useEditSubstanceFieldsConfig';

type ProvidersAndBuyersItem = {
    codigo?: string;
    descripcion?: string;
    proveedor?: {
        nombreQuimico?: string;
        cuit?: string;
        tipo?: string | number;
    };
};

type Contractor = {
    id: number;
    cuit: string;
    idActividad: number;
    cantidadTrabajadores: number;
    tipoEmpresa: number;
};

type Responsible = {
    id: number;
    nombre: string;
    apellido: string;
    cuit: string;
    titulo: string;
    matricula: string;
    entidadOtorgante: string;
    idCargo: number;
    idTipoContratacion: number;
    idRepresentacion: number;
    idTipoProfesional: number;
    cantidadHoras: number;
};

type ResponsiblesAndContractorsValidationResult = {
    isValid: boolean;
    contractorErrors: string[];
    responsibleErrors: string[];
};

export const isSubstanceDataComplete = (substances: any[]): boolean => {
    if (!Array.isArray(substances)) return false;
    
    if (!substances?.length) return false;
    return substances.every((substance) => {
        const { idsUso, cantidadAnual, idUnidadMedida, estudios } = substance;

        const requiresQuantity =
            Array.isArray(idsUso) && idsUso.some((id: number) => !SPECIAL_USAGES_IDS.includes(id));

        // If special usage is required, check for cantidadAnual and idUnidadMedida
        if (requiresQuantity && (cantidadAnual == null || idUnidadMedida == null)) {
            return false;
        }

        // Check estudios is not empty and each estudio is complete
        if (!Array.isArray(estudios) || estudios.length === 0) {
            return false;
        }

        const allEstudiosValid = estudios.every((estudio) => {
            return (
                estudio.id != null &&
                estudio.idTipoFrecuencia != null &&
                estudio.cantidad != null &&
                estudio.detalle !== ''
            );
        });

        return allEstudiosValid;
    });
};

export const validateProvidersAndBuyersItemsArray = (
    items: ProvidersAndBuyersItem[]
): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!items || items.length === 0) {
        errors.push('The array is empty.');
        return { isValid: false, errors };
    }

    items.forEach((item, index) => {
        if (!item?.codigo || item.codigo.trim() === '') {
            errors.push(`items[${index}].codigo is missing`);
        }

        if (!item?.descripcion || item.descripcion.trim() === '') {
            errors.push(`items[${index}].descripcion is missing`);
        }

        if (!item?.proveedor?.nombreQuimico || item.proveedor.nombreQuimico.trim() === '') {
            errors.push(`items[${index}].proveedor.nombreQuimico is missing`);
        }

        if (!item?.proveedor?.cuit || item.proveedor.cuit.trim() === '') {
            errors.push(`items[${index}].proveedor.cuit is missing`);
        }

        if (
            item?.proveedor?.tipo === undefined ||
            item.proveedor.tipo === null ||
            item.proveedor.tipo === ''
        ) {
            errors.push(`items[${index}].proveedor.tipo is missing`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors
    };
};

export const isRadiologicalEquipmentDataComplete = (sustancias: any[]): boolean => {
    if (!Array.isArray(sustancias)) return false;

    // At least one sustancia must have a non-empty equipos array
    const hasAtLeastOneEquipo = sustancias.some(
        (sustancia) => Array.isArray(sustancia.equipos) && sustancia.equipos.length > 0
    );

    if (!hasAtLeastOneEquipo) return false;

    // Now validate all equipos in each sustancia
    return sustancias.every((sustancia) => {
        const { equipos } = sustancia;

        // If equipos is empty or undefined, skip validation (not required for all)
        if (!Array.isArray(equipos) || equipos.length === 0) {
            return true;
        }

        // Validate each equipo object
        return equipos.every((equipo: any) => {
            const hasAllEquipoFields =
                equipo?.id != null &&
                equipo?.marca?.trim() !== '' &&
                equipo?.modelo?.trim() !== '' &&
                equipo?.idTipoEquipo != null &&
                equipo?.idCaracteristica != null &&
                equipo?.idTipoRadiacion != null &&
                equipo?.actividad?.trim() !== '' &&
                equipo?.radioisotopoFuente?.trim() !== '' &&
                equipo?.idTipoFuente != null &&
                equipo?.potenciaKVP != null &&
                equipo?.potenciaMAP != null &&
                equipo?.potenciaMEV != null &&
                equipo?.procesamientoImagenes != null &&
                equipo?.idMetodoProcesamiento != null &&
                // equipo?.observaciones?.trim() !== '' &&
                equipo?.cuitDisometria?.trim() !== '' &&
                equipo?.tipoDisometria?.trim() !== '' &&
                equipo?.metodoDisometria?.trim() !== '' &&
                equipo?.cantidadPersonalDisometria != null;

            const responsables = equipo?.responsables;

            const hasValidResponsables =
                Array.isArray(responsables) &&
                responsables.length > 0 &&
                responsables.every(
                    (resp: any) =>
                        resp?.id != null &&
                        resp?.cuit?.trim() !== '' &&
                        resp?.nombre?.trim() !== '' &&
                        resp?.idTipoResponsable != null &&
                        resp?.permiso?.trim() !== '' &&
                        resp?.matricula?.trim() !== '' &&
                        resp?.fechaExpedicion &&
                        resp?.fechaVencimiento &&
                        resp?.autorizacionIndividual?.trim() !== '' &&
                        resp?.licenciaEspecial != null &&
                        'titulo' in resp
                );

            return hasAllEquipoFields && hasValidResponsables;
        });
    });
};

export const validateResponsiblesAndContractorsCarcinogenicData = (
    contractorsData: Contractor[],
    responsiblesData: Responsible[]
): ResponsiblesAndContractorsValidationResult => {
    const contractorErrors: string[] = [];
    const responsibleErrors: string[] = [];

    const CARGO_VALIDATION_RULES: Record<number, { code: string; required: boolean }> = {
        1: { code: 'R', required: true },
        2: { code: 'H', required: true },
        3: { code: 'M', required: false },
        4: { code: 'C', required: false }
    };

    const cargoCounts: Record<number, number> = {};

    // Helper to validate fields
    const checkFields = (
        obj: any,
        keys: string[],
        prefix: string,
        errorList: string[],
        index: number
    ) => {
        keys.forEach((key) => {
            const value = obj[key];
            if (
                value === undefined ||
                value === null ||
                (typeof value === 'string' && value.trim() === '')
            ) {
                errorList.push(`${prefix}[${index}].${key}`);
            }
        });
    };

    // Contractor checks
    if (!Array.isArray(contractorsData)) {
        contractorErrors.push('contractorsData is empty');
    } else {
        contractorsData.forEach((contractor, index) => {
            checkFields(
                contractor,
                ['id', 'cuit', 'idActividad', 'cantidadTrabajadores', 'tipoEmpresa'],
                'contractorsData',
                contractorErrors,
                index
            );
        });
    }

    // Responsible checks
    if (!Array.isArray(responsiblesData) || responsiblesData.length === 0) {
        responsibleErrors.push('responsiblesData is empty');
    } else {
        responsiblesData.forEach((responsible, index) => {
            const alwaysRequired = [
                'id',
                'nombre',
                'apellido',
                'cuit',
                'idCargo',
                'idTipoContratacion',
                'idRepresentacion'
            ];

            /*
             * La habilitación profesional se le pide al profesional de Higiene y
             * al de Medicina, no al Responsable de los Datos del Formulario: el
             * formulario la deja opcional para ese cargo (useResponsibleFieldsConfig)
             * y el backend también. Exigirla acá frenaba el envío con un "faltan
             * datos" que el usuario no tenía forma de entender.
             */
            const conditionallyRequired = ['cantidadHoras', 'matricula', 'entidadOtorgante', 'titulo'];

            // Validate fields
            checkFields(responsible, alwaysRequired, 'responsiblesData', responsibleErrors, index);

            if (Number(responsible.idCargo) !== RESPONSIBLE_CHARGE_ID_VALUE) {
                checkFields(
                    responsible,
                    conditionallyRequired,
                    'responsiblesData',
                    responsibleErrors,
                    index
                );
            }

            // Count by idCargo
            const cargo = responsible.idCargo;
            if (cargo) {
                cargoCounts[cargo] = (cargoCounts[cargo] || 0) + 1;
            }
        });

        // Validate required cargos and uniqueness
        Object.entries(CARGO_VALIDATION_RULES).forEach(([cargoIdStr, rule]) => {
            const cargoId = Number(cargoIdStr);
            const count = cargoCounts[cargoId] || 0;

            if (rule.required && count === 0) {
                responsibleErrors.push(
                    `Missing required responsible with code '${rule.code}' (idCargo ${cargoId})`
                );
            } else if (count > 1) {
                responsibleErrors.push(
                    `Only one responsible with code '${rule.code}' (idCargo ${cargoId}) is allowed`
                );
            }
        });
    }

    return {
        isValid: contractorErrors.length === 0 && responsibleErrors.length === 0,
        contractorErrors,
        responsibleErrors
    };
};


